import { useCountUp } from '../lib/hooks';
import { Icon, type IconName } from './Icon';

/**
 * بطاقات نظرة سريعة، تفاعلية لا عارضة.
 *
 * القاعدة التي بُنيت عليها: **كل رقم معروض يجب أن يكون قابلًا للنقر**. رقمٌ
 * يقول «سبع مهام متأخرة» ثم يترك القارئ يبحث عنها بنفسه في القائمة يضيف
 * خطوة بدل أن يوفّرها. النقر يُصفّي، والنقر ثانيةً يلغي التصفية.
 *
 * وشريط الحصّة تحت كل بطاقة ليس زينة: العدد وحده لا يقول إن ثلاثة أرباع
 * القائمة عالقة في حالة واحدة.
 *
 * المكوّن مشترك بين شاشات إدارة العملاء كلها — شريط حالات العملاء
 * المحتملين هو أصله، وتعميمه منع نسخه في كل شاشة تريد الشيء نفسه.
 */

export interface InsightItem {
  key: string;
  label: string;
  count: number;
  icon: IconName;
  /** لون البطاقة — من كتالوج الحالات حين يكون لها لون معرَّف. */
  color: string;
  /** ما يُعرض عند التحويم، حين لا يكفي العنوان. */
  hint?: string;
}

export function InsightCards({
  items,
  active,
  onPick,
  showShare = true,
  pending = false,
}: {
  items: InsightItem[];
  /** المفتاح المُصفّى حاليًا، إن وُجد. */
  active?: string;
  onPick?: (key: string | undefined) => void;
  /** يُخفى حين لا تكون البطاقات أجزاءً من كلّ — كأعداد لا تتقاسم مجموعًا. */
  showShare?: boolean;
  pending?: boolean;
}) {
  const total = items.reduce((sum, i) => sum + i.count, 0);

  if (pending) {
    return (
      <div className="insights">
        {items.map((i) => (
          <div key={i.key} className="skeleton" style={{ height: 76, borderRadius: 8 }} />
        ))}
      </div>
    );
  }

  return (
    <div className="insights">
      {items.map((item, index) => (
        <InsightCard
          key={item.key}
          item={item}
          total={showShare ? total : 0}
          active={active === item.key}
          index={index}
          onPick={onPick ? () => onPick(active === item.key ? undefined : item.key) : undefined}
        />
      ))}
    </div>
  );
}

function InsightCard({
  item,
  total,
  active,
  index,
  onPick,
}: {
  item: InsightItem;
  total: number;
  active: boolean;
  index: number;
  onPick?: () => void;
}) {
  const shown = useCountUp(item.count);
  const share = total > 0 ? Math.round((item.count / total) * 100) : 0;

  const content = (
    <>
      <span className="insight-icon">
        <Icon name={item.icon} size={17} />
      </span>

      <span className="insight-body">
        <span className="insight-label">{item.label}</span>
        <span className="insight-value num">{shown}</span>
      </span>

      {total > 0 && (
        <>
          <span className="insight-track" aria-hidden="true">
            <span className="insight-fill" style={{ width: `${share}%` }} />
          </span>
          <span className="insight-share tiny">{`${share}٪`}</span>
        </>
      )}
    </>
  );

  const style = {
    ['--insight-color' as string]: item.color,
    /* تدرّج في الظهور: البطاقات تتتابع بدل أن تقفز معًا. */
    ['--insight-delay' as string]: `${index * 45}ms`,
  };

  /*
   * بطاقة بلا `onPick` تُصيَّر `div` لا `button` معطّلًا.
   *
   * زرٌّ لا يفعل شيئًا يبقى في تسلسل التنقّل بلوحة المفاتيح، فيمرّ عليه من
   * يتنقّل بـTab وينتظر أثرًا لا يقع.
   */
  if (!onPick) {
    return (
      <div className="insight insight-static" style={style} title={item.hint}>
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`insight${active ? ' active' : ''}`}
      style={style}
      onClick={onPick}
      aria-pressed={active}
      title={item.hint ?? (active ? 'اضغط لإلغاء التصفية' : `عرض «${item.label}» وحدهم`)}
    >
      {content}
    </button>
  );
}
